import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import TableServer from '../../components/table/TableServer';
import {
    Block,
    FlexDiv,
    Heading2,
    TextLead,
    BigModal,
    ThemedButton,
} from '@awarego-ui/components';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { capitalizeFirst } from '../../utils/helpers';
import FilterMultiSelect from '../../components/FilterMultiSelect';
import Link from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { List, ListItem, ListItemText } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Popover from '@mui/material/Popover';
import AuditModalContent from '../../components/company/AuditModalContent';
import FilterTextInput from '../../components/FilterTextInput';

const CTX_LABELS = {
    authType: 'Authentication type',
    authScope: 'Authentication scope',
    companyName: 'Company name',
    ip: 'IP',
    integration: 'Integration',
    requestId: 'Request ID',
    partnerPageSwitch: 'Switch from partner portal',
    switchSource: 'Switch origin url',
    webhookId: 'Webhook ID',
    apiKeyId: 'API Key ID',
    packageId: 'Package ID',
    appRequestId: 'App Request ID',
    assessmentId: 'Assessment ID',
    assessmentType: 'Assessment Type',
    assessmentUniqKey: 'Assessment Uniq Key',
    assessmentActive: 'Assessment Active',
    trainingId: 'Training ID',
    trainingActive: 'Training Active',
    listId: 'List ID',
    listName: 'List Name',
    listMode: 'Mode',
    userEmail: 'User Email',
    subscriptionSource: 'Triggered from',
    linkGeneratedBy: 'Sign-In Link generated by',
    linkGeneratedFrom: 'Sign-In Link generated from',
    linkGeneratedAt: 'Sign-In Link generated at',
}

function AuditLog() {
    const { store, companyStore, authStore } = useStores();
    const { currentCompany } = store;
    const { timeZone } = authStore;
    const { operationOptions, subjectOptions } = companyStore;
    const rowsPerPage = companyStore.auditQueryContext.n;

    const [ctxAnchor, setCtxAnchor] = useState(null);
    const ctxOpen = Boolean(ctxAnchor);
    const openCtx  = (e) => setCtxAnchor(e.currentTarget);
    const closeCtx = () => setCtxAnchor(null);

    const [selectedEntry, setSelectedEntry] = useState(null);

    const makeFilterHandler = (key) => (val) =>
        companyStore.applyAuditFilter(key, val, currentCompany?.company_id);

    const onChangePagingParams = useCallback(
        (pageIdx, pageSize) => {
            companyStore.setAuditPagination(pageIdx, pageSize, currentCompany.company_id);
        },
        [companyStore, currentCompany],
    );

    const onClearFilters = () => {
        companyStore.clearAuditFilters(currentCompany.company_id);
    }

    useEffect(() => {
        if (currentCompany) {
            companyStore._loadAuditsRaw({companyId: currentCompany.company_id});
        }
    }, [currentCompany]);

    const columns = useMemo(
        () => [
            {
                Header: 'Action ID',
                accessor: 'id',
                Cell : (x) => ( `#${x.value}` )
            },
            { Header: 'Author', accessor: 'author' },
            {
                Header: 'Date & time',
                accessor: 'created',
                Cell: (x) => moment(x.value).format('LLL'),
            },
            {
                Header: 'Operation',
                accessor: 'operation',
                Cell: (x) => capitalizeFirst(x.value)
            },
            {
                Header: 'Subject',
                accessor: 'subject',
                Cell: (x) => capitalizeFirst(x.value)
            },
            {
                Header: ' ',
                Cell: (x) => (
                    <Link onClick={() => setSelectedEntry(x.row.original)}>
                        <TextLead themed bold link>
                            View details
                        </TextLead>
                    </Link>
                ),
                disableSortBy: true,
            },
        ],
        [],
    );

    const onChangeSort = useCallback(
        (sortBy) => companyStore.setAuditSortBy(sortBy, currentCompany.company_id),
        [currentCompany],
    );

    return (
        <FlexDiv>
            <Block flexGrow="9" mr="20">
                <Heading2 mb={16}>Audit log</Heading2>
                <FlexDiv mb={16} column>
                    <FlexDiv gap={12}>
                        <FilterMultiSelect
                            label="Operation"
                            value={companyStore.auditQueryContext.operation}
                            onChange={makeFilterHandler('operation')}
                            options={operationOptions}
                            sx={{ width: 240 }}

                        />
                        <FilterMultiSelect
                            label="Subject"
                            value={companyStore.auditQueryContext.subject}
                            onChange={makeFilterHandler('subject')}
                            options={subjectOptions}
                            sx={{ width: 240 }}
                        />
                        <FilterTextInput
                            placeholder="Author contains…"
                            label="Author"
                            value={companyStore.auditQueryContext.author || ''}
                            onChange={(val) => makeFilterHandler('author')(val)}
                            sx={{
                                minWidth: 240,
                                '& input::placeholder': {
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    color: alpha('#3b3c3b', 0.6),
                                    opacity: 1,
                                },
                            }}
                        />
                    </FlexDiv>

                    <FlexDiv gap={12} mt={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateTimePicker
                                hiddenLabel
                                value={
                                    companyStore.auditQueryContext.fromDate
                                        ? moment(companyStore.auditQueryContext.fromDate)
                                        : null
                                }
                                ampm={false}
                                dayOfWeekFormatter={(d) => `${d}`}
                                onChange={(v) =>
                                    makeFilterHandler('fromDate')(v?.toDate())
                                }
                                format={`DD/MM/YYYY HH:mm[${timeZone}]`}
                                views={['day', 'hours', 'minutes']}
                                slotProps={{
                                    textField: {
                                        placeholder: 'From',
                                        size: 'small',
                                        sx: {
                                            width: 240,
                                            '& input::placeholder': {
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: '18px',
                                                color: alpha('#3b3c3b', 0.6),
                                                opacity: 1,
                                            },
                                        },
                                    },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />

                            <DateTimePicker
                                hiddenLabel
                                value={
                                    companyStore.auditQueryContext.toDate
                                        ? moment(companyStore.auditQueryContext.toDate)
                                        : null
                                }
                                ampm={false}
                                dayOfWeekFormatter={(d) => `${d}`}
                                onChange={(v) =>
                                    makeFilterHandler('toDate')(v?.toDate())
                                }
                                format={`DD/MM/YYYY HH:mm[${timeZone}]`}
                                views={['day', 'hours', 'minutes']}
                                slotProps={{
                                    textField: {
                                        placeholder: 'To',
                                        size: 'small',
                                        sx: {
                                            width: 240,
                                            '& input::placeholder': {
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: '18px',
                                                color: alpha('#3b3c3b', 0.6),
                                                opacity: 1,
                                            },
                                        },
                                    },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                            <IconButton
                                sx={{height: 42, alignContent: 'center'}}
                                onClick={onClearFilters}
                                aria-label="clear filters"
                            >
                                <ClearIcon />
                            </IconButton>
                        </LocalizationProvider>
                    </FlexDiv>
                </FlexDiv>


                <TableServer
                    columns={columns}
                    data={companyStore.audits.slice()}
                    defaultSortBy={'created'}
                    onChangeSort={onChangeSort}
                    hidePagination={companyStore.auditPages <= 1}
                    rowsTotal={companyStore.auditCount}
                    pagesTotal={companyStore.auditPages}
                    loadingData={companyStore.loadingAudits}
                    onChangePagingParams={onChangePagingParams}
                    initialPageSize={rowsPerPage}
                />
            </Block>

            {selectedEntry && (
                <BigModal
                    ariaHideApp={false}
                    isOpen={!!selectedEntry}
                    onRequestClose={() => setSelectedEntry(null)}
                    heading={
                        <FlexDiv alignCenter>
                            {selectedEntry.type === 'diff' ? 'Changes' : 'Details'}
                            {selectedEntry?.context && (
                                <>
                                    <IconButton size="small" sx={{ ml: 1 }} onClick={openCtx}>
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>

                                    <Popover
                                        open={ctxOpen}
                                        anchorEl={ctxAnchor}
                                        onClose={closeCtx}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    >
                                        <List dense sx={{ px: 2, py: 1, minWidth: 220 }}>
                                            {Object.entries(CTX_LABELS).map(([k, label]) => {
                                                if (selectedEntry.context && k in selectedEntry.context) {
                                                    return (
                                                        <ListItem key={k} disablePadding>
                                                            <ListItemText
                                                                primary={label}
                                                                secondary={String(selectedEntry.context[k])}
                                                            />
                                                        </ListItem>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </List>

                                    </Popover>
                                </>
                            )}
                        </FlexDiv>
                    }
                    width={600}
                    modalContent={<AuditModalContent entry={selectedEntry} />}
                    modalFooterContent={
                        <ThemedButton
                            mode="secondary"
                            onClick={() => setSelectedEntry(null)}
                            text="Close"
                        />
                    }
                />
            )}
        </FlexDiv>
    );
}

export default observer(AuditLog);